import { PropertyName } from "@frui.ts/helpers";
import type { IEntityValidationRules, IEntityValidator, IHasValidation } from "@frui.ts/validation";
import { ValidationErrors, AutomaticEntityValidator, ManualEntityValidator } from "@frui.ts/validation";
import castArray from "lodash/castArray";
import { action, computed, extendObservable, get, observable, runInAction } from "mobx";

export interface IValidatorGroup {
  condition: IValidatorCondition;
  validator: IEntityValidator<any>;
  target: any;
}

export interface IHasValidationAggregator<TTarget> {
  __validation: IEntityValidator<TTarget> & ValidatorAggregator<TTarget>;
}

export type IValidatorCondition = (...rest: any) => boolean;

export default class ValidatorAggregator<TTarget = any> {
  @observable protected validators: Array<IValidatorGroup> = [];
  @observable errors: ValidationErrors<TTarget> = {};
  private manualValidator?: ManualEntityValidator<TTarget>;
  @observable private propertiesValidators: Map<string, Array<IValidatorGroup>> = new Map();

  @computed
  get activeValidators() {
    return this.validators.filter(validator => {
      return validator.condition();
    });
  }

  @computed
  get sourceValidationRules() {
    return this.activeValidators.reduce((acc, activeValidator) => {
      const validator = activeValidator.validator;
      let sourceValidationRules = {};
      if (validator.hasOwnProperty("sourceValidationRules")) {
        sourceValidationRules = (validator as any).sourceValidationRules;
      }

      return {
        ...acc,
        ...sourceValidationRules,
      };
    }, {});
  }

  @computed
  get isErrorsVisible() {
    return this.activeValidators.every(validator => validator.validator.isErrorsVisible);
  }

  set isErrorsVisible(isVisible: boolean) {
    this.validators.forEach(validator => (validator.validator.isErrorsVisible = isVisible));
  }

  public registerValidator(validator: IEntityValidator<any>, target: any, condition?: IValidatorCondition) {
    if (!condition) {
      condition = () => true;
    }

    const validatorGroup = { validator, target, condition };
    this.validators.push(validatorGroup);

    return validatorGroup;
  }

  public unregisterValidation(target: TTarget) {
    const typedTarget = target as TTarget & IHasValidation<TTarget>;

    if (typedTarget?.__validation) {
      const validatorIndex = this.validators.findIndex(validatorGroup => validatorGroup.validator === typedTarget.__validation);

      if (~validatorIndex) {
        this.validators.splice(validatorIndex, 1);
      }
    }
  }

  public registerValidationRules(
    target: TTarget,
    entityValidationRules: IEntityValidationRules<TTarget>,
    errorsImmediatelyVisible: boolean,
    condition?: IValidatorCondition
  ) {
    const validatorSymbol = Symbol("validator");
    interface IHasValidator<TEntity> {
      [validatorSymbol]: AutomaticEntityValidator<TEntity>;
    }

    const typedTarget = target as TTarget & IHasValidator<TTarget>;
    const validator = new AutomaticEntityValidator(target, entityValidationRules, errorsImmediatelyVisible);
    typedTarget[validatorSymbol] = validator;
    const validatorGroup = this.registerValidator(validator, target, condition);

    for (const propertyName in entityValidationRules) {
      if (entityValidationRules.hasOwnProperty(propertyName)) {
        const propertyValidators = this.propertiesValidators.get(propertyName);
        if (propertyValidators) {
          const validators = propertyValidators;
          validators.push(validatorGroup);

          extendObservable(this.errors, {
            get [propertyName]() {
              const errors = validators
                .filter(validator => validator.condition())
                .map(validator => get(validator.validator.errors, propertyName))
                .filter(error => !!error);
              return errors.toString();
            },
          });
        } else {
          this.propertiesValidators.set(propertyName, observable([validatorGroup]));

          // TODO asi bude i tady potřeba zohledňovat zda-li je validator aktivní
          extendObservable(this.errors, {
            get [propertyName]() {
              return get(validator.errors, propertyName);
            },
          });
        }
      }
    }
  }

  public registerManualValidator<TTarget>(target: TTarget, errorsImmediatelyVisible: boolean, condition?: IValidatorCondition) {
    if (!this.manualValidator) {
      this.manualValidator = new ManualEntityValidator(errorsImmediatelyVisible);
      this.registerValidator(this.manualValidator, target, condition);
    }
  }

  @computed
  get isValid() {
    return this.activeValidators.reduce((isValid, validator) => {
      return isValid && validator.validator.isValid;
    }, true);
  }

  @action.bound
  validate() {
    runInAction(() => this.activeValidators.map(validator => (validator.validator.isErrorsVisible = true)));

    return this.isValid;
  }

  @action.bound
  clearErrors() {
    this.manualValidator?.clearErrors();
  }

  @action.bound
  addError(propertyName: PropertyName<TTarget>, message: string) {
    if (!this.manualValidator) {
      throw new Error("You use manual validator before its attach");
    }

    const validator = this.manualValidator;

    // TODO Asi se to bude muset propojit s chybami s automatic validátoru
    extendObservable(this.errors, {
      get [propertyName]() {
        return get(validator.errors, propertyName);
      },
    });

    this.manualValidator.addError(propertyName, message);
  }

  @action.bound
  removeError(propertyName: PropertyName<TTarget>) {
    this.manualValidator?.removeError(propertyName);
  }

  @action.bound
  clear() {
    this.validators = [];
    this.manualValidator = undefined;
    this.propertiesValidators.clear();
  }
}

export function attachValidatorAggregator<TTarget>(
  target: TTarget,
  validators?: IHasValidation<any> | Array<IHasValidation<any>>,
  condition?: IValidatorCondition
) {
  const typedTarget = target as TTarget & IHasValidationAggregator<TTarget>;
  if (!typedTarget.__validation) {
    typedTarget.__validation = new ValidatorAggregator<TTarget>();
  }

  validators = validators ? castArray(validators) : [];
  validators.forEach(validator => typedTarget.__validation.registerValidator(validator.__validation, validator, condition));

  return typedTarget;
}

export function attachAutomaticValidator<TTarget>(
  target: TTarget,
  entityValidationRules: IEntityValidationRules<TTarget>,
  errorsImmediatelyVisible = false,
  condition?: IValidatorCondition
) {
  const typedTarget = target as TTarget & IHasValidationAggregator<TTarget>;
  if (!typedTarget.__validation) {
    typedTarget.__validation = new ValidatorAggregator<TTarget>();
  }

  typedTarget.__validation.registerValidationRules(target, entityValidationRules, errorsImmediatelyVisible, condition);
  return typedTarget;
}

export function attachManualValidator<TTarget>(target: TTarget, errorsImmediatelyVisible = false) {
  const typedTarget = target as TTarget & IHasValidationAggregator<TTarget>;
  if (!typedTarget.__validation) {
    typedTarget.__validation = new ValidatorAggregator<TTarget>();
  }

  typedTarget.__validation.registerManualValidator(target, errorsImmediatelyVisible);
  return typedTarget;
}
